import { Notification } from '@vwfs-bronson/bronson-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type IncidentNotificationProps = {
    page: 'landing' | 'dashboard';
};

type IncidentNotificationCmsContent = {
    contentId?: string;
    title?: string;
    messageBody?: string;
    status?: 'info' | 'warning' | 'error' | 'success' | 'marketing1' | 'marketing2';
    displayLocation?: string;
    active?: string | boolean;
};

type BronsonNotificationStatus = 'info' | 'warning' | 'error' | 'success' | undefined;

const INCIDENT_NAMESPACE = 'incydent-notification-cms';
const SESSION_STORAGE_PREFIX = 'portal-incydent-notification.dismissed.';

const normalizeText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const toBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value !== 'string') {
        return false;
    }

    const normalized = value.trim().toLowerCase();
    return normalized === 'on' || normalized === 'true' || normalized === '1' || normalized === 'active';
};

const normalizeDisplayLocation = (value: unknown): 'landing' | 'dashboard' | 'all' => {
    const normalized = normalizeText(value).toLowerCase().replace(/\s+/g, '').replace(/-/g, '');

    if (normalized === 'landingpage' || normalized === 'landing') {
        return 'landing';
    }

    if (normalized === 'dashboardpage' || normalized === 'dashboard') {
        return 'dashboard';
    }

    return 'all';
};

const canDisplayOnPage = (displayLocation: 'landing' | 'dashboard' | 'all', page: 'landing' | 'dashboard'): boolean => {
    return displayLocation === 'all' || displayLocation === page;
};

const createContentIdentifier = (
    cmsConfig: IncidentNotificationCmsContent,
    normalizedDisplayLocation: 'landing' | 'dashboard' | 'all',
): string => {
    const normalizedContentId = normalizeText(cmsConfig.contentId);
    const fallbackTitle = normalizeText(cmsConfig.title).toLowerCase().replace(/\s+/g, '-');

    return `${normalizedContentId || fallbackTitle}-${normalizedDisplayLocation}`;
};

const getRecordFromUnknown = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, unknown>;
};

const extractIncidentConfig = (resource: unknown): IncidentNotificationCmsContent | null => {
    const root = getRecordFromUnknown(resource);
    if (!root) {
        return null;
    }
    const title = normalizeText(root.title);
    const messageBody = normalizeText(root.messageBody);
    if (!title || !messageBody) {
        return null;
    }
    return {
        contentId: normalizeText(root.contentId),
        title,
        messageBody,
        status: normalizeText(root.status) as 'info' | 'warning' | 'error' | 'success',
        displayLocation: normalizeText(root.displayLocation),
        active: root.active as string | boolean | undefined,
    };
};

const getMarketingTheme = (status?: 'info' | 'warning' | 'error' | 'success' | 'marketing1' | 'marketing2') => {
    switch (status) {
        case 'marketing1':
            return 1;
        case 'marketing2':
            return 2;
        default:
            return undefined;
    }
};

const getStatus = (
    status?: 'info' | 'warning' | 'error' | 'success' | 'marketing1' | 'marketing2',
): BronsonNotificationStatus => {
    return ['info', 'warning', 'error', 'success'].includes(status || '')
        ? (status as BronsonNotificationStatus)
        : undefined;
};

export const IncidentNotification: React.FC<IncidentNotificationProps> = ({ page }) => {
    const { i18n, t: cms } = useTranslation(INCIDENT_NAMESPACE);
    const [isOpen, setIsOpen] = useState(false);

    const cmsConfig = useMemo(() => {
        const resource = cms('notification', {
            returnObjects: true,
            // defaultValue: previewIncidentNotificationContent,
        });
        return extractIncidentConfig(resource);
    }, [i18n, i18n.languages]);

    const normalizedDisplayLocation = useMemo<'landing' | 'dashboard' | 'all'>(() => {
        if (!cmsConfig) {
            return 'all';
        }

        return normalizeDisplayLocation(cmsConfig.displayLocation);
    }, [cmsConfig]);

    const contentIdentifier = useMemo(() => {
        if (!cmsConfig) {
            return '';
        }

        return createContentIdentifier(cmsConfig, normalizedDisplayLocation);
    }, [cmsConfig, normalizedDisplayLocation]);

    const shouldDisplay = useMemo(() => {
        if (!cmsConfig) {
            return false;
        }

        const isActive = toBoolean(cmsConfig.active);

        if (!isActive || !canDisplayOnPage(normalizedDisplayLocation, page)) {
            return false;
        }

        try {
            return sessionStorage.getItem(`${SESSION_STORAGE_PREFIX}${contentIdentifier}`) !== 'true';
        } catch {
            return true;
        }
    }, [cmsConfig, contentIdentifier, normalizedDisplayLocation, page]);

    useEffect(() => {
        setIsOpen(shouldDisplay);
    }, [shouldDisplay]);

    const handleClose = useCallback(() => {
        if (!cmsConfig) {
            setIsOpen(false);
            return;
        }

        try {
            sessionStorage.setItem(`${SESSION_STORAGE_PREFIX}${contentIdentifier}`, 'true');
        } finally {
            setIsOpen(false);
        }
    }, [cmsConfig, contentIdentifier]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', closeOnEscape);

        return () => {
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [isOpen, handleClose]);

    if (!cmsConfig) {
        return null;
    }

    const title = normalizeText(cmsConfig.title);
    const messageBody = normalizeText(cmsConfig.messageBody);

    if (!title || !messageBody) {
        return null;
    }

    return (
        <Notification
            title={title}
            status={getStatus(cmsConfig.status)}
            data-marketing-theme={getMarketingTheme(cmsConfig.status)}
            visible={isOpen}
            onCloseClick={handleClose}
            testId={'incident-notification-banner'}
        >
            <div
                dangerouslySetInnerHTML={{
                    __html: messageBody,
                }}
            />
        </Notification>
    );
};
